import { Trans, useTranslation } from "react-i18next";
import type { ActionKey } from "../components/toast/ToastProvider";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/DriveFolderUploadOutlined";
import { useCallback, useEffect, useRef, useState } from "react";
import { PageContainer, PageHeader } from "../components/PageHeader";
import EditorCard from "../components/template/EditorCard";
import { EditDrawer } from "../components/EditDrawer";
import {
  foyerApi,
  type BatchItemView,
  type BatchView,
  type IngestReadiness,
} from "../http/foyer";
import { packageFindingText } from "../components/distribution/findings";
import { formatBatchEta, type BatchStatus } from "@shared/bulk-ingest";
import { uploadInBlocks } from "../lib/blockUpload";
import { tokens } from "../theme";
import { useFormat } from "../i18n/format";
import { translateKey } from "../i18n/translate";
import { describeError, useToast } from "../components/toast";
import { PermissionGate } from "../auth/access";
import {
  AUTHOR_PACKAGES,
  AUTHOR_TITLES,
} from "../components/distribution/catalogueGates";

/**
 * Bulk ingest — many titles through the lifecycle that works for one
 * (`BE-31`).
 *
 * ## The screen has one job the server cannot do for it: be honest about time
 *
 * Encore is serial and a transcode is roughly a quarter of an hour, so a
 * 300-title package is **days**, before Foyer yields a single slot back to the
 * portal. That number is on the first screen, next to the button, because the
 * alternative is somebody discovering it on day two — and because it is the
 * reason pause and resume exist at all rather than only cancel.
 *
 * ## Findings come before anything is dispatched
 *
 * Scan is read-only and repeatable: it pairs what is actually in storage,
 * validates it, and creates nothing. An operator fixes a sidecar, re-uploads
 * it, rescans, and watches the finding go — with no catalogue rows minted along
 * the way. Only Start creates anything, and by then every refusal has already
 * been shown with the file it concerns.
 */

const BLOCKING_MARK = "✕ ";
const NOTE_MARK = "• ";

const STATUS_TONE: Record<BatchStatus, string> = {
  empty: tokens.inkMuted,
  draft: tokens.inkMuted,
  all_blocked: tokens.error,
  running: tokens.brand,
  paused: tokens.warning,
  cancelled: tokens.inkMuted,
  completed: tokens.success,
  partly_failed: tokens.warning,
};

function StatusChip({ status }: { status: BatchStatus }) {
  const { t } = useTranslation("content");
  const tone = STATUS_TONE[status];
  return (
    <Chip
      size="small"
      variant="outlined"
      label={t(`bulkIngestPage.status.${status}`)}
      sx={{ borderColor: tone, color: tone, fontWeight: "fontWeightBold" }}
      data-testid={`batch-status-${status}`}
    />
  );
}

function FindingList({ findings }: { findings: BatchItemView["findings"] }) {
  const { t } = useTranslation("content");
  if (findings.length === 0) return null;
  return (
    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
      {findings.map((f, i) => (
        <Typography
          variant="micro"
          key={`${f.code}-${i}`}
          sx={{
            color: f.severity === "blocking" ? tokens.error : tokens.inkMuted,
          }}
        >
          {f.severity === "blocking" ? BLOCKING_MARK : NOTE_MARK}
          {packageFindingText(t, f)}
        </Typography>
      ))}
    </Stack>
  );
}

function ItemRow({ item }: { item: BatchItemView }) {
  const { t } = useTranslation("content");
  return (
    <Box
      sx={{
        py: 1.25,
        borderBottom: `1px solid ${tokens.divider}`,
      }}
      data-testid={`batch-item-${item.key}`}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="bodySm"
            sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
          >
            {item.title}
          </Typography>
          <Typography
            variant="micro"
            sx={{ color: tokens.inkMuted, fontFamily: "fontFamilyMono" }}
          >
            {item.videoPath}
            {item.assetId ? ` → ${item.assetId}.mp4` : ""}
          </Typography>
        </Box>
        <Chip
          size="small"
          variant="outlined"
          label={
            item.blocked
              ? t("bulkIngestPage.item.refused")
              : item.phase
                ? item.phase
                : t("bulkIngestPage.item.notStarted")
          }
          color={
            item.blocked
              ? "error"
              : item.phase === "completed"
                ? "success"
                : item.phase === "failed"
                  ? "error"
                  : "default"
          }
        />
      </Stack>

      {item.phase === "transcoding" && item.progress !== null && (
        <LinearProgress
          variant="determinate"
          value={Math.min(100, Math.max(0, item.progress))}
          sx={{ mt: 1, borderRadius: 1 }}
        />
      )}

      <FindingList findings={item.findings} />

      {/* Two different failures, kept apart. `error` is Foyer refusing to
          create the row or move the file; `jobError` is the pipeline. Folding
          them together loses which half to go and look at. */}
      {item.error && (
        <Typography variant="micro" sx={{ color: tokens.error, mt: 0.5 }}>
          {BLOCKING_MARK}
          {item.error}
        </Typography>
      )}
      {item.jobError && (
        <Typography variant="micro" sx={{ color: tokens.error, mt: 0.5 }}>
          {BLOCKING_MARK}
          {item.jobError}
        </Typography>
      )}
    </Box>
  );
}

/** Relative path inside the drop, so a folder upload keeps its layout. */
function relativePathOf(file: File): string {
  const withPath = file as File & { webkitRelativePath?: string };
  const raw = withPath.webkitRelativePath || file.name;
  // A directory picker prefixes the chosen folder's own name. Dropping it is
  // what makes `The Film/movie.mp4` pair with `The Film/metadata.json` rather
  // than everything living one level too deep.
  const slash = raw.indexOf("/");
  return slash > 0 ? raw.slice(slash + 1) : raw;
}

function BatchDetail({
  batch,
  readiness,
  onChanged,
}: {
  batch: BatchView;
  readiness: IngestReadiness | null;
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [packages, setPackages] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const folderInput = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    foyerApi
      .listIngestEntitlements()
      .then((a) => setPackages(a.collections))
      .catch(() => setPackages([]));
  }, []);

  const draft = batch.intent === "draft";
  const { rollup } = batch;

  const name = batch.name;
  const ACTIONS: Record<string, [string, ActionKey]> = {
    packages: [
      t("bulkIngestPage.toasts.packagesUpdated", { name }),
      "updateBatchPackages",
    ],
    scan: [t("bulkIngestPage.toasts.rescanned", { name }), "rescanBatch"],
    upload: [t("bulkIngestPage.toasts.uploaded", { name }), "uploadBatchFiles"],
    start: [t("bulkIngestPage.toasts.started", { name }), "startBatch"],
    pause: [t("bulkIngestPage.toasts.paused", { name }), "pauseBatch"],
    resume: [t("bulkIngestPage.toasts.resumed", { name }), "resumeBatch"],
    cancel: [t("bulkIngestPage.toasts.cancelled", { name }), "cancelBatch"],
  };

  async function run(label: string, work: () => Promise<unknown>) {
    setError(null);
    setBusy(label);
    const [done, failed] = ACTIONS[label] ?? [
      t("bulkIngestPage.toasts.updated", { name }),
      "updateBatch",
    ];
    try {
      await work();
      await onChanged();
      toast.success(done);
    } catch (e) {
      const err = e as { code?: string; message: string; unknown?: string[] };
      toast.error(describeError(t, failed, e, { name: batch.name }));
      setError(
        err.code === "unknown_entitlements"
          ? translateKey(t, "errors:unknown_entitlements", {
              unknown: (err.unknown ?? []).join(", "),
            })
          : err.code === "storage_not_configured" ||
              err.code === "nothing_to_start"
            ? translateKey(t, `errors:${err.code}`)
            : err.message
      );
    } finally {
      setBusy(null);
    }
  }

  async function uploadFolder(files: FileList) {
    setError(null);
    const list = Array.from({ length: files.length }, (_, i) => files[i]);
    for (const [index, file] of list.entries()) {
      setBusy(
        t("bulkIngestPage.busy.uploading", {
          index: index + 1,
          total: list.length,
          name: file.name,
        })
      );
      const path = relativePathOf(file);
      // Sequential on purpose. The portal's own bulk client uploads with
      // concurrency 1, and matching it keeps a package that took an hour to
      // upload from saturating a customer's line.
      const { uploadUrl } = await foyerApi.batchUploadUrl(batch.id, path);
      await uploadInBlocks(uploadUrl, file, {
        contentType: file.type || "application/octet-stream",
      });
    }
    setBusy(t("bulkIngestPage.busy.scanning"));
    await foyerApi.scanBatch(batch.id);
    await onChanged();
    setBusy(null);
  }

  // The drawer around this supplies the name, "Started by" line and status.
  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2, typography: "meta" }}>
          {error}
        </Alert>
      )}

      {readiness && !readiness.ready && (
        <Alert severity="warning" sx={{ mb: 2, typography: "meta" }}>
          {readiness.explanation}
        </Alert>
      )}

      {/* The honest first screen. */}
      <Stack
        direction="row"
        spacing={3}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Counter
          label={t("bulkIngestPage.counters.titles")}
          value={rollup.total}
        />
        <Counter
          label={t("bulkIngestPage.counters.refused")}
          value={rollup.blocked}
          tone={tokens.error}
        />
        <Counter
          label={t("bulkIngestPage.counters.waiting")}
          value={rollup.waiting}
        />
        <Counter
          label={t("bulkIngestPage.counters.running")}
          value={rollup.running}
          tone={tokens.brand}
        />
        <Counter
          label={t("bulkIngestPage.counters.published")}
          value={rollup.completed}
          tone={tokens.success}
        />
        <Counter
          label={t("bulkIngestPage.counters.failed")}
          value={rollup.failed}
          tone={tokens.error}
        />
      </Stack>

      {rollup.remaining > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 2, typography: "meta" }}
          data-testid="batch-eta"
        >
          <Trans
            t={t}
            i18nKey="bulkIngestPage.eta"
            values={{ eta: formatBatchEta(rollup.etaMinutes) }}
            components={{ b: <b /> }}
          />
        </Alert>
      )}

      {batch.packageFindings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, typography: "meta" }}>
          <FindingList findings={batch.packageFindings} />
        </Alert>
      )}

      {draft && (
        <Box
          sx={{
            border: `1px dashed ${tokens.divider}`,
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            mb: 2,
            bgcolor: tokens.brandTintSoft,
          }}
        >
          <UploadIcon sx={{ fontSize: 28, color: tokens.inkMuted }} />
          <Typography
            variant="bodySm"
            sx={{ fontWeight: "fontWeightBold", mt: 0.5 }}
          >
            {t("bulkIngestPage.drop.title")}
          </Typography>
          <Typography variant="micro" sx={{ color: tokens.inkMuted, mt: 0.5 }}>
            <Trans
              t={t}
              i18nKey="bulkIngestPage.drop.hint"
              components={{
                code: (
                  <Box component="span" sx={{ fontFamily: "fontFamilyMono" }} />
                ),
              }}
            />
          </Typography>

          <Box sx={{ mt: 1.5, textAlign: "left" }}>
            <Typography
              variant="micro"
              sx={{ fontWeight: "fontWeightBold", mb: 0.5 }}
            >
              {t("bulkIngestPage.drop.packagesTitle")}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {packages.map((p) => (
                <PermissionGate
                  key={p.id}
                  {...AUTHOR_PACKAGES}
                  deniedProps={{ disabled: true }}
                >
                  <Chip
                    size="small"
                    label={p.name}
                    data-testid={`batch-package-${p.id}`}
                    color={
                      batch.entitlements.includes(p.id) ? "primary" : "default"
                    }
                    variant={
                      batch.entitlements.includes(p.id) ? "filled" : "outlined"
                    }
                    onClick={() =>
                      void run("packages", () =>
                        foyerApi.setBatchPackages(
                          batch.id,
                          batch.entitlements.includes(p.id)
                            ? batch.entitlements.filter((x) => x !== p.id)
                            : [...batch.entitlements, p.id]
                        )
                      )
                    }
                  />
                </PermissionGate>
              ))}
            </Stack>
            {batch.entitlements.length === 0 && (
              <Typography
                variant="micro"
                sx={{ color: tokens.warning, mt: 0.5 }}
                data-testid="batch-no-package-warning"
              >
                {packages.length === 0
                  ? t("bulkIngestPage.drop.noPackagesAtAll")
                  : t("bulkIngestPage.drop.noPackageSelected")}
              </Typography>
            )}
          </Box>

          <input
            ref={folderInput}
            type="file"
            hidden
            multiple
            data-testid="batch-folder"
            // Non-standard and deliberately used: a package IS a folder, and
            // asking somebody to select 900 files by hand is not a workflow.
            {...({
              webkitdirectory: "",
              directory: "",
            } as Record<string, string>)}
            onChange={(e) => {
              const files = e.target.files;
              e.target.value = "";
              if (files && files.length > 0) {
                void run("upload", () => uploadFolder(files));
              }
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            sx={{ mt: 1.5 }}
          >
            <PermissionGate {...AUTHOR_TITLES} deniedProps={{ disabled: true }}>
              <Button
                variant="contained"
                size="small"
                disabled={Boolean(busy)}
                onClick={() => folderInput.current?.click()}
                data-testid="batch-choose-folder"
              >
                {t("bulkIngestPage.drop.chooseFolder")}
              </Button>
            </PermissionGate>
            <PermissionGate {...AUTHOR_TITLES} deniedProps={{ disabled: true }}>
              <Button
                size="small"
                disabled={Boolean(busy)}
                onClick={() =>
                  void run("scan", () => foyerApi.scanBatch(batch.id))
                }
                data-testid="batch-rescan"
              >
                {t("bulkIngestPage.drop.rescan")}
              </Button>
            </PermissionGate>
          </Stack>
        </Box>
      )}

      {busy && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress sx={{ borderRadius: 1 }} />
          <Typography
            variant="micro"
            sx={{ color: tokens.inkMuted, mt: 0.5 }}
            data-testid="batch-busy"
          >
            {busy}
          </Typography>
        </Box>
      )}

      <Divider sx={{ mb: 1.5 }} />

      <Stack
        direction="row"
        spacing={1}
        sx={{ mb: 2 }}
        flexWrap="wrap"
        useFlexGap
      >
        {draft && (
          <PermissionGate {...AUTHOR_TITLES} deniedProps={{ disabled: true }}>
            <Button
              variant="contained"
              size="small"
              disabled={Boolean(busy) || rollup.total === 0}
              onClick={() =>
                void run("start", () => foyerApi.startBatch(batch.id))
              }
              data-testid="batch-start"
            >
              {t("bulkIngestPage.actions.start", {
                count: rollup.total - rollup.blocked,
              })}
            </Button>
          </PermissionGate>
        )}
        {batch.intent === "started" && (
          <PermissionGate {...AUTHOR_TITLES} deniedProps={{ disabled: true }}>
            <Button
              size="small"
              disabled={Boolean(busy)}
              onClick={() =>
                void run("pause", () =>
                  foyerApi.setBatchIntent(batch.id, "pause")
                )
              }
              data-testid="batch-pause"
            >
              {t("bulkIngestPage.actions.pause")}
            </Button>
          </PermissionGate>
        )}
        {batch.intent === "paused" && (
          <PermissionGate {...AUTHOR_TITLES} deniedProps={{ disabled: true }}>
            <Button
              size="small"
              variant="contained"
              disabled={Boolean(busy)}
              onClick={() =>
                void run("resume", () =>
                  foyerApi.setBatchIntent(batch.id, "resume")
                )
              }
              data-testid="batch-resume"
            >
              {t("bulkIngestPage.actions.resume")}
            </Button>
          </PermissionGate>
        )}
        {batch.intent !== "cancelled" && batch.intent !== "draft" && (
          <PermissionGate {...AUTHOR_TITLES} deniedProps={{ disabled: true }}>
            <Button
              size="small"
              color="error"
              disabled={Boolean(busy)}
              onClick={() =>
                void run("cancel", () =>
                  foyerApi.setBatchIntent(batch.id, "cancel")
                )
              }
              data-testid="batch-cancel"
            >
              {t("bulkIngestPage.actions.cancel")}
            </Button>
          </PermissionGate>
        )}
      </Stack>

      {(batch.intent === "paused" || batch.intent === "cancelled") && (
        <Alert severity="info" sx={{ mb: 2, typography: "meta" }}>
          {t("bulkIngestPage.stoppedNote")}
        </Alert>
      )}

      {batch.items.length === 0 ? (
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {t("bulkIngestPage.nothingPaired")}
        </Typography>
      ) : (
        batch.items.map((item) => <ItemRow key={item.key} item={item} />)
      )}
    </>
  );
}

function Counter({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: string;
}) {
  return (
    <Box>
      <Typography
        sx={{
          typography: "titleLg",
          fontWeight: "fontWeightBold",
          color: tone ?? tokens.ink,
        }}
      >
        {value}
      </Typography>
      <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
        {label}
      </Typography>
    </Box>
  );
}

export default function BulkIngestPage() {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const fmt = useFormat();
  const [batches, setBatches] = useState<BatchView[] | null>(null);
  const [readiness, setReadiness] = useState<IngestReadiness | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const openBatch = batches?.find((b) => b.id === openId) ?? null;

  const load = useCallback(async () => {
    try {
      const answer = await foyerApi.listBatches();
      setBatches(answer.batches);
      setReadiness(answer.readiness);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    // A batch is a multi-day object, so this polls slowly: the interesting
    // change is a phase moving, and that happens on a 30 s server tick.
    const timer = setInterval(() => void load(), 20_000);
    return () => clearInterval(timer);
  }, [load]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("bulkIngestPage.eyebrow")}
        title={t("bulkIngestPage.title")}
        subtitle={t("bulkIngestPage.subtitle")}
        actions={
          <PermissionGate {...AUTHOR_TITLES}>
            <Button
              variant="contained"
              size="small"
              onClick={() => setCreating(true)}
              data-testid="batch-new"
            >
              {t("bulkIngestPage.actions.newPackage")}
            </Button>
          </PermissionGate>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2, typography: "meta" }}>
          {error}
        </Alert>
      )}

      {batches === null && (
        <Stack alignItems="center" sx={{ py: 4 }}>
          <CircularProgress size={24} />
        </Stack>
      )}

      {batches?.length === 0 && (
        <EditorCard eyebrow={t("bulkIngestPage.noPackagesYet.eyebrow")}>
          <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
            {t("bulkIngestPage.noPackagesYet.body")}
          </Typography>
        </EditorCard>
      )}

      <Stack spacing={2}>
        {batches?.map((batch) => (
          <EditorCard
            key={batch.id}
            eyebrow={batch.name}
            hint={
              batch.rollup.remaining > 0
                ? t("bulkIngestPage.cardHintLeft", {
                    total: batch.rollup.total,
                    completed: batch.rollup.completed,
                    eta: formatBatchEta(batch.rollup.etaMinutes),
                  })
                : t("bulkIngestPage.cardHint", {
                    total: batch.rollup.total,
                    completed: batch.rollup.completed,
                  })
            }
            action={<StatusChip status={batch.rollup.status} />}
          >
            <Button
              size="small"
              onClick={() => setOpenId(batch.id)}
              data-testid={`batch-open-${batch.id}`}
            >
              {t("bulkIngestPage.actions.open")}
            </Button>
          </EditorCard>
        ))}
      </Stack>

      {/* Opening a package edits it, so it slides in from the right. Wide:
          the counters, findings and per-title rows want the room. onClose
          runs only after the slide-out, so clearing openId there is safe. */}
      {openBatch && (
        <EditDrawer
          open
          onClose={() => setOpenId(null)}
          title={openBatch.name}
          subtitle={t("bulkIngestPage.startedBy", {
            name: openBatch.createdBy,
            date: fmt.date(openBatch.createdAt, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            }),
          })}
          headerAside={<StatusChip status={openBatch.rollup.status} />}
          width={900}
          data-testid={`batch-detail-${openBatch.id}`}
          actions={(dismiss) => (
            <Button size="small" onClick={() => dismiss()}>
              {t("common:buttons.close")}
            </Button>
          )}
        >
          <BatchDetail
            batch={openBatch}
            readiness={readiness}
            onChanged={load}
          />
        </EditDrawer>
      )}

      <Dialog
        open={creating}
        onClose={() => setCreating(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t("bulkIngestPage.create.title")}</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t("bulkIngestPage.create.name")}
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText={t("bulkIngestPage.create.nameHint")}
            inputProps={{ "data-testid": "batch-name" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate {...AUTHOR_TITLES} deniedProps={{ disabled: true }}>
            <Button
              variant="contained"
              disabled={name.trim() === ""}
              data-testid="batch-create"
              onClick={() => {
                void (async () => {
                  try {
                    const { batch } = await foyerApi.createBatch({
                      name: name.trim(),
                    });
                    setName("");
                    setCreating(false);
                    toast.success(
                      t("bulkIngestPage.toasts.created", { name: batch.name })
                    );
                    await load();
                    setOpenId(batch.id);
                  } catch (e) {
                    setError((e as Error).message);
                    toast.error(
                      describeError(t, "createBatch", e, { name: name.trim() })
                    );
                  }
                })();
              }}
            >
              {t("common:buttons.create")}
            </Button>
          </PermissionGate>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
